/* eslint-disable @typescript-eslint/no-unused-vars */
import { STPageType, ReorderOpType } from '@tencent/slide-sdk-pb-model';
import { record } from '../utils/recorder';
import { getShapeKeyframe } from '../keyframe/get-shape-keyframe';
import { merge } from '../utils/protoutil';
import { cloneDeep } from '../utils/clone-deep';
import { Rule } from '../mutation/base/mutation-rules';
import { GroupMutation } from '../mutation/shape';
import { DataType, DataTypeUtils } from '../data';
import { createIdsMapFromShapes } from '../utils/id-map-generator';
import { createMutations } from '../mutation/mutation-factory';
import { SetPagePropertiesMutation, PageDeltaUtils } from '../mutation/page/set-page-properties-mutation';
import { RandomIDGenerator } from '../utils/id-generator';
import { ComputeGroupTransformMutation } from '../mutation/shape/compute-group-transform-mutation';
import { MoveShapeMutation } from '../mutation/shape/move-shape-mutation';
import { DiagramGroupShapeUtils } from '../mutation/base/smart-art-diagram-utils';
import { MutationAPI } from './mutation-api';
import type { ICTSlideTransition } from '@tencent/slide-sdk-pb-model';
import type { ISmartArtDataModel } from '../model/smart-art-data-model';
import type { IDrawingShapeData, IPageDataDelta, ILayoutDataDelta, IMasterDataDelta, ISlideDataDelta } from '../data';
import type { PageDataModel } from '../model/page-data-model';
import type { IDrawingShapeDataModel, IGroupShapeDataModel } from '../model/group-shape-data-model';
import type { DeepReadonly } from 'ts-essentials';

export class PageMutationAPI<DataModel extends PageDataModel<any, any>> extends MutationAPI<DataModel> {
    /**
     * 删除页内形状
     * @param shapes 待删除的形状
     */
    @record()
    public deleteShapes(shapes: IDrawingShapeDataModel[]) {
        if (!shapes.length) return;
        this.guard((presentation) =>
            Rule.forEach(shapes, (shape) => Rule.shape.should.existInPage(presentation, shape.id, this.model.id)),
        );

        this.transaction(() => {
            this.model.store.spTree?.api.deleteShapes(shapes);
        });
    }

    /**
     * smartart 转组合
     * 1. 根据smartArt 生成组合model；
     * 2. this.addShapes(groupShape)添加组合形状；
     * 3. 重新计算transform并删除smarart形状；
     * @param smartArtShape smartart 数据
     */
    @record()
    public convertSmartArtToGroup(smartArtShape: ISmartArtDataModel) {
        this.transaction(() => {
            const shapeTree = this.model.store.getShapeTree();
            // 这里克隆一份smartArtShape，保证原始数据的正确性
            const groupShapeData = DiagramGroupShapeUtils.convertSmartArtToIGroupShapeData(
                smartArtShape?.getCloneData(),
            );
            // 添加组合类形状
            const groupData = this.addShapes([groupShapeData]);
            // 计算组合形状的变换
            this.request([
                new ComputeGroupTransformMutation({
                    pageId: this.model.id,
                    groupId: groupData[0].id,
                    parentId: shapeTree.id,
                }),
            ]);
            // 删除smartart形状
            this.deleteShapes([smartArtShape]);
        });
    }

    /**
     * 组合元素，执行一下操作：
     * 1. 移除待组合元素的动画；
     * 2. 组合的 Transform 根据待组合元素的 Transform 计算得出；
     * 2. 组合的层级是待组合元素的最高层级；
     * @param shapes 待调整的元素集合，必须是 Page 的 shapeList 的子集，且自身可组合（占位符、表格等禁止组合）
     */
    @record()
    public group(shapes: IDrawingShapeDataModel[]) {
        this.guard((presentation) =>
            Rule.forEach(shapes, (shape) =>
                Rule.every(
                    Rule.shape.is.childOfShapeTreeRoot(presentation, shape.id, this.model.id),
                    Rule.shape.is.notTypeOf(presentation, shape.id, DataType.GraphicalObjectFrameTable),
                ),
            ),
        );

        this.transaction(() => {
            const presentation = this.model.context.getPresentation();
            const groupShapeId = presentation.getIDGenerator().next();
            const shapeTree = this.model.store.getShapeTree();

            const idsSet = new Set(shapes.map((shape) => shape.id));
            if (idsSet.size <= 1) {
                return;
            }

            const { shapeList } = shapeTree;

            let groupIndex = shapeList.length - 1;
            while (groupIndex >= 0 && !idsSet.has(shapeList[groupIndex].id)) {
                groupIndex -= 1;
            }

            const shapeIds: string[] = [];
            for (let i = groupIndex; i >= 0; i--) {
                if (idsSet.has(shapeList[i].id)) {
                    const shapeId = shapeList[i].id;
                    if (i !== groupIndex) {
                        this.request(
                            [
                                new MoveShapeMutation({
                                    pageId: this.model.id,
                                    shapeId,
                                    srcParentId: shapeTree.id,
                                    destParentId: shapeTree.id,
                                    srcIndex: i,
                                    destIndex: groupIndex,

                                    // TODO: deprecated
                                    opType: ReorderOpType.Back,
                                    destShapeId: '',
                                }),
                            ],
                            true,
                        );
                    }

                    shapeIds.push(shapeId);
                    groupIndex -= 1;
                }
            }

            shapeIds.reverse();
            groupIndex += 1;

            shapes.forEach((shape) => this.model.timing?.api.removeAnimationsByShapeId(shape.id));
            this.transaction(() => {
                this.request([
                    new GroupMutation({
                        pageId: this.model.id,
                        groupShapeId,
                        shapeIds,
                        isSmartArt: false,
                        parentId: shapeTree.id,
                        index: groupIndex,
                    }),
                ]);
                this.request([
                    new ComputeGroupTransformMutation({
                        pageId: this.model.id,
                        groupId: groupShapeId,
                        parentId: shapeTree.id,
                    }),
                ]);
            });
        });
    }

    /**
     * 解组组合，并执行以下操作：
     * 1. 解组后，子元素会重新计算 Transform；
     * 2. 如果组合有填充，且子元素设置为组合填充，解组后的子元素会设置为组合的填充；
     * @param groupShape 待解组的组合
     */
    @record()
    public ungroup(groupShape: IGroupShapeDataModel) {
        this.guard((presentation) => Rule.shape.is.childOfShapeTreeRoot(presentation, groupShape.id, this.model.id));
        groupShape.api.ungroup();
    }

    /**
     *
     * 调整形状层级，相对于各形状的自身位置进行调整。
     * 调整后，形状之间的相对顺序保持不变。
     * @param shapes 待调整的形状集合，必须是 Page 的 shapeList 的子集。
     * @param opType 调整类型，支持：前移、后移、置顶、置底
     */
    @record()
    public reorderShapes(shapes: IDrawingShapeDataModel[], opType: ReorderOpType) {
        return this.model.store.getShapeTree().api.reorderShapes(shapes, opType);
    }

    /**
     * 调整形状层级，统一调整到同一指定位置。
     * 调整后，形状之间的相对顺序保持不变
     * @param shapes 待调整的形状集合，必须是 Page 的 shapeList 的子集。
     * @param toIndex 目标位置
     */
    @record()
    public moveShapes(shapes: IDrawingShapeDataModel[], toIndex: number) {
        return this.model.store.getShapeTree().api.moveShapes(shapes, toIndex);
    }

    // FIXME：当添加多个形状，多个形状有相同 id 时，会生成重复 id。不过暂没有发现 api 调用中有此种情况
    @record()
    public addShapes(addShapeDataArray: DeepReadonly<IDrawingShapeData>[]): IDrawingShapeDataModel[] {
        const newShapes = cloneDeep(addShapeDataArray) as IDrawingShapeData[];
        for (const shapeData of newShapes) {
            this.normalizeShapeData(shapeData);
        }
        const idsMap = createIdsMapFromShapes(newShapes, this.model.context.getPresentation().getIDGenerator());
        const context = {
            presentation: this.model.parent.store.data,
            page: this.model.store.data,
            idFactory: (oldId: string) => idsMap.get(oldId) ?? oldId,
        };
        const n = this.model.shapeList.length;
        this.forEachInTransaction(newShapes, (shapeData) => {
            this.transaction(() => {
                this.modifyPlaceholderIndex(shapeData);
                const mutations = getShapeKeyframe(
                    shapeData,
                    context,
                    this.model.store.getShapeTree().id,
                    this.model.shapeList.length,
                );
                this.request(createMutations(mutations));
            });
        });

        return this.model.shapeList.slice(n);
    }

    @record()
    public updateName(name: string) {
        this.transaction(() => {
            this.request([
                new SetPagePropertiesMutation({
                    pageId: this.model.id,
                    pageType: DataTypeUtils.toSTPageType(this.model.type),
                    pageProperties: { name },
                }),
            ]);
        });
    }

    @record()
    public updateProps<T extends IPageDataDelta>(pageDelta: T) {
        const pageType = DataTypeUtils.toSTPageType(this.model.type);
        this.transaction(() => {
            this.request([
                new SetPagePropertiesMutation({
                    pageId: this.model.id,
                    pageType,
                    ...this.getMutationDeltaFromPageData(pageType, pageDelta),
                }),
            ]);
        });
    }

    @record()
    public updateTransition(transitionDelta: ICTSlideTransition | null) {
        // transition数据在中台是替换的，这里生成transition的全量数据
        const pageData = transitionDelta
            ? merge({ transition: cloneDeep(this.model.store.data.transition) }, { transition: transitionDelta })
            : { transition: transitionDelta };

        this.updateProps(pageData);
    }

    protected createPlaceholderIndex(): number {
        const placeholderIndices = this.model.placeholders.map((shape) => shape.getOwnedData().nvPr.ph?.idx ?? 0);
        return Math.max(...placeholderIndices, 0) + 1;
    }

    private getMutationDeltaFromPageData<T extends IPageDataDelta>(pageType: STPageType, pageDelta: T) {
        switch (pageType) {
            case STPageType.slide: {
                return PageDeltaUtils.fromSlideDataDelta(pageDelta as ISlideDataDelta);
            }
            case STPageType.slideLayout: {
                return PageDeltaUtils.fromLayoutDataDelta(pageDelta as ILayoutDataDelta);
            }
            case STPageType.slideMaster: {
                return PageDeltaUtils.fromMasterDataDelta(pageDelta as IMasterDataDelta);
            }
            default: {
                return {};
            }
        }
    }

    /**
     * 修正 shapeData, 保证 shapeData 数据准确：
     * shapeData 里必须有 nvPr、 spPr 和 id
     */
    private normalizeShapeData(shape: Partial<IDrawingShapeData>) {
        const shapeData = shape;
        // 没有 id 给出默认id
        if (!shapeData.id) {
            shapeData.id = RandomIDGenerator.next();
        }
        if (!shapeData.nvPr) {
            shapeData.nvPr = {};
        }
        if (!shapeData.spPr) {
            shapeData.spPr = {};
        }
    }

    /**
     * 重置重复的占位符 index，防止错误数据进入 mutation。
     */
    private modifyPlaceholderIndex(shape: IDrawingShapeData) {
        if (shape.type === DataType.GroupShape) {
            return;
        }
        const newPlaceholderData = shape.nvPr.ph;
        if (!newPlaceholderData?.idx) {
            return;
        }
        for (const placeholder of this.model.placeholders) {
            if (newPlaceholderData.idx === placeholder.getOwnedData().nvPr.ph?.idx) {
                newPlaceholderData.idx = this.createPlaceholderIndex();
                return;
            }
        }
    }
}
